import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Spinner } from "reactstrap";
import { humanizeKey, describeLeaf, actionLabel, isGroupNode, groupLabel } from "./ruleLogicFormatter";

const humanize = humanizeKey;

const ruleKey = (templateId, ruleName) => `${templateId}::${ruleName}`;

/** Recursively renders a conditions_tree node as an indented, plain-English list. */
function ConditionNode({ node, factsByKey, depth = 0 }) {
  if (!node) return null;

  if (!isGroupNode(node)) {
    return <li>{describeLeaf(node, factsByKey)}</li>;
  }

  return (
    <li style={{ listStyle: depth === 0 ? "none" : undefined, marginLeft: depth === 0 ? -18 : 0 }}>
      <div style={{ fontStyle: "italic", color: "#555" }}>{groupLabel(node)}</div>
      <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
        {node.children.map((child, i) => (
          <ConditionNode key={i} node={child} factsByKey={factsByKey} depth={depth + 1} />
        ))}
      </ul>
    </li>
  );
}

/** Plain-language "When: ... Then: ..." rendering of a rule's logic. */
function RuleLogic({ rule, factsByKey, actionsByKey }) {
  return (
    <div style={{
      marginTop: 6, fontSize: 12, background: "#f5f5f5",
      padding: 10, borderRadius: 6,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>When:</div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        <ConditionNode node={rule.conditions_tree} factsByKey={factsByKey} />
      </ul>
      <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4 }}>Then:</div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {(rule.actions || []).map((a, i) => (
          <li key={i}>{actionLabel(a, actionsByKey)}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Browse rules_template rows for a strategy_type, grouped by category, and
 * let the caller select any subset of individual rules across categories
 * (not just a whole category/template at once).
 *
 * onImport receives the flattened array of selected rule objects.
 */
export default function TemplateCategoryBrowser({ strategyType, onImport, importing }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Map()); // key -> rule object
  const [logicOpen, setLogicOpen] = useState(() => new Set());
  const [factsByKey, setFactsByKey] = useState({});
  const [actionsByKey, setActionsByKey] = useState({});

  // Fact/action label registries are global (not per-strategy) — fetch once.
  useEffect(() => {
    axios.get("/rules/facts")
      .then(res => {
        const map = {};
        (res.data || []).forEach(f => { map[f.fact_key] = f; });
        setFactsByKey(map);
      })
      .catch(() => {});
    axios.get("/rules/actions")
      .then(res => {
        const map = {};
        (res.data || []).forEach(a => { map[a.action_key] = a; });
        setActionsByKey(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    setExpanded(new Set());
    setSelected(new Map());
    setLogicOpen(new Set());
    axios.get(`/rules/templates/${strategyType}`)
      .then(res => {
        const rows = (res.data || []).map(row => {
          let rules = [];
          try { rules = JSON.parse(row.template_json) || []; } catch { rules = []; }
          return { ...row, rules };
        });
        // Pin the starter bundle first so new users see it immediately.
        rows.sort((a, b) => {
          const aStarter = a.category === "GETTING_STARTED" ? 0 : 1;
          const bStarter = b.category === "GETTING_STARTED" ? 0 : 1;
          return aStarter - bStarter;
        });
        setCategories(rows);

        // Auto-expand and pre-select the starter bundle for a one-click import.
        const starter = rows.find(r => r.category === "GETTING_STARTED");
        if (starter) {
          setExpanded(new Set([starter.id]));
          setSelected(new Map(starter.rules.map(r => [ruleKey(starter.id, r.rule_name), r])));
        }

        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [strategyType]);

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleLogic = (key) => {
    setLogicOpen(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleRule = (templateId, rule) => {
    const key = ruleKey(templateId, rule.rule_name);
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, rule);
      return next;
    });
  };

  const selectedCount = selected.size;

  const handleImport = () => {
    onImport(Array.from(selected.values()));
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 30 }}><Spinner color="primary" /></div>;
  }

  if (categories.length === 0) {
    return (
      <p style={{ color: "#888", textAlign: "center", padding: 20 }}>
        No templates found for {strategyType}.
      </p>
    );
  }

  return (
    <div>
      {categories.map(cat => {
        const isOpen = expanded.has(cat.id);
        const isStarter = cat.category === "GETTING_STARTED";
        return (
          <div
            key={cat.id}
            style={{
              marginBottom: 10,
              border: isStarter ? "2px solid #28a745" : "1px solid #e0e0e0",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <div
              onClick={() => toggleExpanded(cat.id)}
              style={{
                padding: "12px 16px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: isStarter ? "#f2fbf4" : (isOpen ? "#f0f7ff" : "#fafafa"),
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  {cat.template_name}
                  {isStarter && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, color: "#fff", background: "#28a745",
                      padding: "2px 8px", borderRadius: 10, textTransform: "uppercase", letterSpacing: 0.3,
                    }}>
                      Recommended for new strategies
                    </span>
                  )}
                </div>
                {cat.description && (
                  <div style={{ fontSize: 12, color: "#666", marginTop: 3 }}>
                    {cat.description}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#999", whiteSpace: "nowrap", marginLeft: 12 }}>
                {cat.rules.length} rule{cat.rules.length !== 1 ? "s" : ""} {isOpen ? "▲" : "▼"}
              </div>
            </div>

            {isOpen && (
              <div style={{ borderTop: "1px solid #eee" }}>
                {cat.rules.map(rule => {
                  const key = ruleKey(cat.id, rule.rule_name);
                  const isSelected = selected.has(key);
                  const logicKey = key;
                  return (
                    <div
                      key={key}
                      style={{
                        padding: "10px 16px",
                        borderBottom: "1px solid #f0f0f0",
                        background: isSelected ? "#f7fbff" : "#fff",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRule(cat.id, rule)}
                          style={{ marginTop: 3 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>
                            {humanize(rule.rule_name)}
                          </div>
                          {rule.description && (
                            <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                              {rule.description}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleLogic(logicKey)}
                            style={{
                              marginTop: 4, fontSize: 11, color: "#007bff",
                              background: "none", border: "none", padding: 0, cursor: "pointer",
                            }}
                          >
                            {logicOpen.has(logicKey) ? "Hide logic" : "View logic"}
                          </button>
                          {logicOpen.has(logicKey) && (
                            <RuleLogic rule={rule} factsByKey={factsByKey} actionsByKey={actionsByKey} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      <div style={{
        position: "sticky", bottom: 0, background: "#fff",
        borderTop: "1px solid #e0e0e0", padding: "12px 4px", marginTop: 10,
        display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12,
      }}>
        <span style={{ fontSize: 13, color: "#666" }}>
          {selectedCount} rule{selectedCount !== 1 ? "s" : ""} selected
        </span>
        <Button
          color="primary"
          disabled={selectedCount === 0 || importing}
          onClick={handleImport}
        >
          {importing ? "Importing..." : `Import ${selectedCount || ""} Selected Rule${selectedCount !== 1 ? "s" : ""}`}
        </Button>
      </div>
    </div>
  );
}
